// @flow
import moment from "moment"
import { omit } from "ramda"
// eslint-disable-next-line kiwicom/prefer-nitro-translate-function
import translate from "@kiwicom/frontend-app/scripts/tools/translate"

import type Journey from "common/records/Journey"
import { DATE_FORMAT } from "../components/constants"
import type {
  Document,
  PassengerApi,
  PassengerEdit,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingPassengerEdit/services/types"

const CASE_INSENSITIVE_KEYS = ["first_name", "last_name", "middle_name"]

export const countDifferenceBetweenValues = (a: string, b: string) => {
  const longer = a.length > b.length ? a : b
  const shorter = a.length <= b.length ? a : b

  return Array.from(longer).reduce((acc, next, i) => {
    if (i < shorter.length && next.toLowerCase() === shorter[i].toLowerCase()) {
      return acc
    }
    return acc + 1
  }, 0)
}

export const getChangedLetterCount = (newUserData: PassengerApi, oldUserData: PassengerApi) => {
  const firstNameChanges = countDifferenceBetweenValues(
    newUserData.first_name,
    oldUserData.first_name,
  )
  const middleNameChanges = countDifferenceBetweenValues(
    newUserData.middle_name,
    oldUserData.middle_name,
  )
  const lastNameChanges = countDifferenceBetweenValues(newUserData.last_name, oldUserData.last_name)

  return firstNameChanges + middleNameChanges + lastNameChanges
}

export const formatUser = (user: PassengerEdit): PassengerApi => ({
  birth_date: user.birth,
  document_expiry: user.document.expiration,
  document_number: user.document.code,
  first_name: user.firstname.trim(),
  middle_name: user.middlename ? user.middlename.trim() : "",
  last_name: user.lastname.trim(),
  nationality: user.nationality,
  title: user.title,
})

export const compareUsers = (newUserData: PassengerApi, oldUserData: PassengerApi) => {
  const compared = {}

  CASE_INSENSITIVE_KEYS.forEach(i => {
    if (newUserData[i].toLowerCase() !== oldUserData[i].toLowerCase()) {
      compared[i] = newUserData[i]
    }
  })

  Object.keys(omit(CASE_INSENSITIVE_KEYS, newUserData)).forEach(i => {
    if (newUserData[i] !== oldUserData[i]) {
      compared[i] = newUserData[i]
    }
  })

  if (compared.document_expiry && compared.document_expiry === "no-expiration") {
    compared.document_expiry = null
  }

  return compared
}

type RangeType = "day" | "month" | "year"
export const rangeTranslation = (type: RangeType, lower: number, upper: number) => {
  let rangeTypeKey = ""
  switch (type) {
    case "day":
      rangeTypeKey = __("booking.passenger.id_day.label")
      break
    case "month":
      rangeTypeKey = __("booking.passenger.id_month.label")
      break
    case "year":
      rangeTypeKey = __("booking.passenger.id_year.label")
      break
    default:
      break
  }
  return {
    tKey: __("forms.must_be_in_range"),
    values: {
      what: translate(rangeTypeKey),
      lower,
      upper,
    },
  }
}

export const getCurrentYear = () =>
  moment()
    .utc()
    .toDate()
    .getUTCFullYear()

export const getLatestFlightDeparture = (journey: Journey) =>
  journey
    .getFlights()
    .last()
    .getIn(["arrival", "when", "local"])

export const hasInvalidCode = (originalDocument: Document, currentDocument: Document) =>
  originalDocument.code && (!currentDocument.code || currentDocument.code.length < 4)

export const hasExpirationDate = (expiryDate: ?string) =>
  expiryDate && expiryDate !== "no-expiration"

export const formatDate = (date: ?string) =>
  moment(date, DATE_FORMAT).isValid() ? moment(date, DATE_FORMAT).format(DATE_FORMAT) : date

export const formatUserNameForValidation = (name: ?string) =>
  name ? name.trim().toLowerCase() : name
